import { useEffect, useMemo, useState } from 'react';

import { isApplicationKiosk } from '@lib/core/service/consts';
import { testConnectionQuality } from '@lib/core/service/utils';
import MixpanelTracker from '@lib/tools/dat/mixpanel';
import { localeCommon } from '@lib/tools/locale/source/web/common';
import { useAddons } from '@lib/tools/views/hooks';

import { Text } from '@components/web/src/foundations/Text/Text';
import { COLORS } from '@components/web/src/styles';
import * as S from '@components/web/src/templates/Loading/LoadingSpinner/styles';

export const LOADING_SPINNER_VARIANTS = {
  CATALOG_PAGE: 'catalogPage',
  PLAIN: 'plain',
  QUIZ_COMPLETED: 'quizCompleted',
  SCANNER: 'scanner',
} as const;

export const SLOW_PAGE_SCENARIOS = {
  isLowSpeedAPI: 'isLowSpeedAPI',
  isLowSpeedConnection: 'isLowSpeedConnection',
} as const;

export type LoadingSpinnerVariant = (typeof LOADING_SPINNER_VARIANTS)[keyof typeof LOADING_SPINNER_VARIANTS];

interface Props {
  variant?: LoadingSpinnerVariant;
}

export const LoadingSpinner = ({ variant }: Props) => {
  const { lowSpeedWarning, scanFidelityCardWarning, loadingPage } = localeCommon;
  const {
    lowSpeedWarningTitleForWidget,
    lowSpeedWarningTitleForKiosk,
    lowSpeedWarningDescription,
    slowApiWarningDescription,
    slowApiWarningTitle,
  } = lowSpeedWarning;
  const { scanFidelityCardWarningTitle, scanFidelityCardWarningDescription } = scanFidelityCardWarning;
  const { defaultTitle, finishedTestTitle, catalogTitle } = loadingPage;

  const isLocaleDataExist = !!(lowSpeedWarning && scanFidelityCardWarning && loadingPage);

  const { isEnableKioskFidelityScanAddon } = useAddons();
  const [slowPageScenario, setSlowPageScenario] = useState(null);

  useEffect(() => {
    const lowSpeedTimer = setTimeout(async () => {
      const scenario = await testConnectionQuality();
      setSlowPageScenario(scenario);

      MixpanelTracker.events.trackSlowConnection();
    }, 4000);

    return () => clearTimeout(lowSpeedTimer);
  }, []);

  const { titleText, subtitleText } = useMemo(() => {
    if (variant === LOADING_SPINNER_VARIANTS.PLAIN) {
      return { subtitleText: null, titleText: null };
    }

    if (slowPageScenario) {
      const isLowSpeedConnection = slowPageScenario === SLOW_PAGE_SCENARIOS.isLowSpeedConnection;
      const lowSpeedWarningTitle = isApplicationKiosk ? lowSpeedWarningTitleForKiosk : lowSpeedWarningTitleForWidget;

      return {
        subtitleText: isLowSpeedConnection ? lowSpeedWarningDescription : slowApiWarningDescription,
        titleText: isLowSpeedConnection ? lowSpeedWarningTitle : slowApiWarningTitle,
      };
    }

    const variantMap = {
      [LOADING_SPINNER_VARIANTS.SCANNER]: {
        subtitleText: scanFidelityCardWarningDescription,
        titleText: isEnableKioskFidelityScanAddon ? scanFidelityCardWarningTitle : '',
      },
      [LOADING_SPINNER_VARIANTS.QUIZ_COMPLETED]: {
        subtitleText: null,
        titleText: finishedTestTitle,
      },
      [LOADING_SPINNER_VARIANTS.CATALOG_PAGE]: {
        subtitleText: null,
        titleText: catalogTitle,
      },
    } as const;

    return (
      variantMap[variant] || {
        subtitleText: null,
        titleText: defaultTitle,
      }
    );
  }, [variant, slowPageScenario]);

  return (
    <S.LoadingSpinnerContainer>
      <S.SpinnerWrapper>
        <S.Spinner />
      </S.SpinnerWrapper>
      <S.TextContainer>
        {isLocaleDataExist && (
          <>
            {titleText && (
              <Text
                color={COLORS.gray[900]}
                fontType="grace"
                size="h5"
                text={titleText}
                textAlign="center"
                weight="bold"
              />
            )}
            {subtitleText && <Text color={COLORS.gray[900]} size="body2" text={subtitleText} textAlign="center" />}
          </>
        )}
      </S.TextContainer>
    </S.LoadingSpinnerContainer>
  );
};
